// AdminTab.js — Администрирование, Профиль компании и Управление персоналом
import React, { useState, useEffect, useRef } from "react";
import { Users, Shield, Plus, Edit3, Trash2, Check, X, Save, Download, History, EyeOff, Eye, Building2, Mail, Phone, Lock, CheckCircle, UserCheck, AlertCircle, Search, UserPlus, KeyRound, Smartphone, Tablet, Laptop, Monitor, MapPin, ShieldCheck, ShieldOff, AlertTriangle, LogOut, Activity, Copy } from "lucide-react";
import { UAParser } from "ua-parser-js";
import { QRCodeSVG } from "qrcode.react";
import { phoneDigits, formatPhone, isValidPhone } from './phone';
import { useTheme, uid, fmtDay, ROLE_LABELS, ROLE_PERMS, PERM_LABELS, Card, Btn, PageHeader, TabSwitch, downloadAuditLog, addAuditLog, CURRENCIES, getCurrencyLabel, HelpTooltip, stripCyrillic } from "./shared";
import { S, setLang, getLang } from "./strings";
import { HELP_TEXTS } from "./HelpText";
import * as api from "./api";

export default function AdminTab({ data, uOrg, user, perms, isCompact, meta, uMeta, globalUser, setGlobalUser, orgId, changeLang, apiSync, onDeleteOrg }) {
  const t = useTheme();
  const [sec, setSec] = useState('profile');
  const isOwner = perms.all;

  return (
    <div>
      <PageHeader title={S().admin.title} sub={S().admin.subtitle} />
      <TabSwitch
        tabs={[
          { id: 'profile', label: S().admin.profileTab, icon: Building2 },
          { id: 'users', label: S().admin.usersTab, icon: Users },
          // Этап 2: вкладка "Безопасность" перенесена в отдельный таб MyAccountTab
          // в sidebar. Доступна всем ролям (включая sub-users), не только owner-у.
          { id: 'system', label: S().admin.systemTab, icon: Shield }
        ]}
        active={sec}
        onChange={setSec}
      />

      <div style={{ marginTop: 18 }}>
        {sec === 'profile' && <AProfile data={data} uOrg={uOrg} user={user} isOwner={isOwner} isCompact={isCompact} changeLang={changeLang} onDeleteOrg={onDeleteOrg} orgId={orgId} meta={meta} uMeta={uMeta} globalUser={globalUser} setGlobalUser={setGlobalUser} />}
        {sec === 'users' && <AUsers data={data} uOrg={uOrg} user={user} isOwner={isOwner} isCompact={isCompact} meta={meta} uMeta={uMeta} globalUser={globalUser} setGlobalUser={setGlobalUser} orgId={orgId} />}
        {sec === 'system' && <ASys data={data} uOrg={uOrg} user={user} isOwner={isOwner} isCompact={isCompact} />}
      </div>
    </div>
  );
}

// Stage 11/Этап 3.2: функция ASecurity удалена. Раньше она была контейнером
// для TwoFactorCard + SessionsCard + SecurityEventsCard на вкладке "Безопасность"
// внутри AdminTab. На Этапе 2 вкладка "Безопасность" перенесена в отдельный таб
// MyAccountTab (доступный всем ролям), и ASecurity стала dead code — теперь удалена.
// Сами три карточки остались как named exports — используются в MyAccountTab.

// ══════════════════════ Вкладка: ПРОФИЛЬ И КОМПАНИЯ ══════════════════════
function AProfile({ data, uOrg, user, isOwner, isCompact, changeLang, onDeleteOrg, orgId, meta, uMeta, globalUser, setGlobalUser }) {
  const t = useTheme();
  
  // Примечание: поля email и phone больше НЕ редактируются в блоке "Информация о компании".
  // Они остаются в БД (data.email/data.phone) для обратной совместимости и не трогаются при сохранении.
  // Персональный телефон пользователя (globalUser.phone) теперь живёт в блоке "Мой аккаунт".
  const [form, setForm] = useState({
    name: data.name || '',
    details: data.details || '',
    currency: data.currency || 'KZT'
  });

  // При переключении организации — обновляем форму данными новой орг
  useEffect(() => {
    setForm({
      name: data.name || '',
      details: data.details || '',
      currency: data.currency || 'KZT'
    });
    setPw('');
  }, [data.name, data.details, data.currency]);

  const [pw, setPw] = useState('');

  // ── Смена личного телефона (globalUser.phone) ──────────────────
  const [phoneEdit, setPhoneEdit] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState(phoneDigits(globalUser?.phone || ''));
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneErr, setPhoneErr] = useState('');
  const [phoneOk, setPhoneOk] = useState('');

  useEffect(() => {
    setPhoneDraft(phoneDigits(globalUser?.phone || ''));
  }, [globalUser?.phone]);

  const phoneSave = async () => {
    setPhoneErr('');
    const digits = phoneDigits(phoneDraft);
    if (!isValidPhone(digits)) {
      setPhoneErr(S().admin.phoneMinDigits || 'Минимум 7 цифр');
      return;
    }
    setPhoneLoading(true);
    try {
      const updated = await api.updateProfile({ phone: digits });
      if (setGlobalUser) setGlobalUser(prev => ({ ...(prev || {}), ...updated }));
      setPhoneOk(S().admin.phoneSaved || 'Телефон сохранён');
      setPhoneEdit(false);
      setTimeout(() => setPhoneOk(''), 3000);
    } catch (e) {
      setPhoneErr((e && e.message) || (S().admin.phoneSaveErr || 'Не удалось сохранить'));
    } finally {
      setPhoneLoading(false);
    }
  };

  const phoneCancel = () => {
    setPhoneEdit(false);
    setPhoneDraft(phoneDigits(globalUser?.phone || ''));
    setPhoneErr('');
  };

  // ── Смена личного email (globalUser.email) ───────────────────
  // Шаги: 0 = idle (показывает текущий email), 1 = ввод нового email, 2 = ввод OTP
  const [mailStep, setMailStep] = useState(0);
  const [newMail, setNewMail] = useState('');
  const [mailOtp, setMailOtp] = useState('');
  const [mailLoading, setMailLoading] = useState(false);
  const [mailError, setMailError] = useState('');
  const [mailSuccess, setMailSuccess] = useState('');
  // Stage 11: для смены email требуется подтверждение текущим паролем (защита
  // от email-takeover атаки через украденный access-токен).
  const [mailCurrentPw, setMailCurrentPw] = useState('');

  const mailChangeCancel = () => {
    setMailStep(0);
    setNewMail('');
    setMailOtp('');
    setMailError('');
    setMailCurrentPw(''); // не оставляем пароль в state после отмены
  };

  const mailRequestCode = async () => {
    setMailError('');
    const trimmed = newMail.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setMailError(S().emailChange.newEmailPh || 'Введите корректный email');
      return;
    }
    if (globalUser && trimmed === (globalUser.email || '').toLowerCase()) {
      setMailError(S().emailChange.sameEmail || 'Новый email совпадает с текущим');
      return;
    }
    if (!mailCurrentPw) {
      setMailError('Введите текущий пароль');
      return;
    }
    setMailLoading(true);
    try {
      await api.requestEmailChange(mailCurrentPw, trimmed, getLang());
      setMailCurrentPw(''); // очищаем после успешной отправки
      setMailStep(2);
    } catch (e) {
      const msg = e && e.message ? e.message : '';
      // Rate limit: бэк возвращает retryAfter
      const m = msg.match(/(\d+)\s*сек/);
      if (m && S().emailChange.rateLimit) {
        setMailError(S().emailChange.rateLimit(m[1]));
      } else {
        setMailError(msg || (S().emailChange.sendError || 'Не удалось отправить код'));
      }
    } finally {
      setMailLoading(false);
    }
  };

  const mailConfirm = async () => {
    setMailError('');
    const code = mailOtp.trim();
    if (code.length !== 6) {
      setMailError(S().emailChange.invalidCode || 'Неверный код');
      return;
    }
    setMailLoading(true);
    try {
      const res = await api.confirmEmailChange(code, getLang());
      if (res && res.user) {
        if (setGlobalUser) setGlobalUser(prev => ({ ...(prev || {}), ...res.user }));
        setMailSuccess(S().emailChange.successMsg || 'Email успешно изменён');
        setMailStep(0);
        setNewMail('');
        setMailOtp('');
        setTimeout(() => setMailSuccess(''), 4000);
      } else {
        setMailError(S().emailChange.invalidCode || 'Неверный код');
      }
    } catch (e) {
      const msg = (e && e.message) || '';
      if (/expired|истёк|истек/i.test(msg)) {
        setMailError(S().emailChange.expired || 'Код истёк, запросите новый');
      } else {
        setMailError(msg || (S().emailChange.invalidCode || 'Неверный код'));
      }
    } finally {
      setMailLoading(false);
    }
  };

  const [pwSaving, setPwSaving] = useState(false);
  // Stage 11: для смены пароля теперь требуется текущий пароль (защита от угона access-токена)
  const [currentPw, setCurrentPw] = useState('');
  const [pwError, setPwError] = useState('');

  const save = () => {
    if (!isOwner) return;
    // Если меняется пароль — обязательно нужен текущий
    if (pw && pw.length >= 8 && !currentPw) {
      setPwError('Для смены пароля укажите текущий пароль');
      return;
    }
    setPwError('');
    uOrg(p => {
      // email и phone больше не редактируются здесь — сохраняем как есть из p
      const nd = { ...p, name: form.name, details: form.details, currency: form.currency };
      if (pw && user) {
        nd.users = nd.users.map(u => u.id === user.id ? { ...u, password: pw } : u);
      }
      return nd;
    });

    // Обновляем имя организации в meta.orgs (чтобы sidebar и org-select показывали правильное имя)
    if (orgId && uMeta) {
      uMeta(p => ({
        ...p,
        orgs: (p.orgs || []).map(o => o.id === orgId ? { ...o, name: form.name } : o),
      }));
      // Обновляем на сервере (таблица Org)
      api.updateOrg(orgId, { name: form.name, currency: form.currency }).catch(err => {
        console.error('[ADMIN] Update org on server failed:', err.message);
      });
    }

    // Обновляем пароль пользователя на сервере через безопасный endpoint
    // (Stage 11: проверка текущего пароля + ротация всех refresh-токенов)
    if (pw && pw.length >= 8 && currentPw) {
      setPwSaving(true);
      api.changePassword(currentPw, pw).then((res) => {
        console.log(`[ADMIN] Password changed; revoked ${res.revokedSessions} other sessions`);
        setCurrentPw('');
      }).catch(err => {
        console.error('[ADMIN] Password update failed:', err.message);
        setPwError(err.message || 'Не удалось сменить пароль');
      }).finally(() => setPwSaving(false));
    }

    addAuditLog(uOrg, user, {
      type: 'profile_update',
      desc: `Обновлен профиль компании: ${form.name}`,
      details: `Валюта: ${form.currency}`
    });

    setPw('');
    alert(S().admin.saveSuccess);
  };

  // Отслеживаем изменения формы для показа кнопки "Сохранить"
  const hasChanges = form.name !== (data.name || '') ||
    form.details !== (data.details || '') ||
    form.currency !== (data.currency || 'KZT') || pw.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 18 }}>
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Building2 size={18} color={t.accent} /> {S().admin.companyInfo}
          </div>

          {!isOwner && <div style={{ fontSize: 12, color: t.accentOrange, background: `${t.accentOrange}15`, padding: '8px 12px', borderRadius: 8, display: 'flex', gap: 8 }}><AlertCircle size={16}/> {S().admin.ownerOnly}</div>}

          {/* СЕЛЕКТОР ВАЛЮТЫ */}
          <div style={{ padding: '12px 14px', background: `${t.accent}10`, borderRadius: 10, border: `1px dashed ${t.accent}40` }}>
            <label style={{ fontSize: 13, color: t.text, marginBottom: 6, display: 'block', fontWeight: 700 }}>{S().admin.baseCurrency}</label>
            <select disabled={!isOwner} value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} style={{ fontSize: 15, padding: '8px 12px', width: '100%', opacity: isOwner ? 1 : 0.6, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, cursor: isOwner ? 'pointer' : 'default' }}>
              {CURRENCIES.map(c => <option key={c.id} value={c.id}>{getCurrencyLabel(c.id)}</option>)}
            </select>
            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6 }}>{S().admin.currencyHint}</div>
          </div>

          {/* СЕЛЕКТОР ЯЗЫКА */}
          <div style={{ padding: '12px 14px', background: `${t.accent}10`, borderRadius: 10, border: `1px dashed ${t.accent}40` }}>
            <label style={{ fontSize: 13, color: t.text, marginBottom: 6, display: 'block', fontWeight: 700 }}>{S().admin.langLabel}</label>
            <select value={getLang()} onChange={e => changeLang(e.target.value)} style={{ fontSize: 15, padding: '8px 12px', width: '100%', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, cursor: 'pointer' }}>
              <option value="ru">Русский</option>
              <option value="kz">Қазақша</option>
              <option value="en">English</option>
            </select>
          </div>

          <div>
            <label style={{ fontSize: 13, color: t.textMuted, marginBottom: 6, display: 'block', fontWeight: 600 }}>{S().admin.companyName}</label>
            <input disabled={!isOwner} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={S().admin.companyPlaceholder} style={{ fontSize: 15, padding: '10px 14px', width: '100%', opacity: isOwner ? 1 : 0.6, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text }} />
          </div>

          {/* Email и телефон компании перенесены в блок "Мой аккаунт" (личный телефон пользователя).
              Email организации остаётся в data.email для обратной совместимости, но не редактируется тут. */}

          <div>
            <label style={{ fontSize: 13, color: t.textMuted, marginBottom: 6, display: 'block', fontWeight: 600 }}>{S().admin.detailsLabel}</label>
            <textarea disabled={!isOwner} value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} placeholder={S().admin.detailsPlaceholder} rows={3} style={{ fontSize: 14, padding: '10px 14px', width: '100%', opacity: isOwner ? 1 : 0.6, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text }} />
          </div>

          {/* Удаление организации */}
          {isOwner && onDeleteOrg && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 12, borderTop: `1px solid ${t.border}`, flexWrap: 'wrap' }}>
              <button onClick={onDeleteOrg} style={{
                padding: '8px 16px', borderRadius: 8, background: t.accentRed, color: '#fff',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                border: 'none', flexShrink: 0, whiteSpace: 'nowrap',
              }}>
                <Trash2 size={14} /> {S().deletion.deleteOrg}
              </button>
              <span style={{ fontSize: 11, color: t.textMuted, lineHeight: 1.4, flex: 1, minWidth: 150 }}>
                {S().deletion.deleteOrgHint}
              </span>
            </div>
          )}
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserCheck size={18} color={t.accentPurple} /> {S().admin.myAccount}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, background: t.bgInput, borderRadius: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: t.accentPurple, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800 }}>
                {user ? user.name.charAt(0).toUpperCase() : 'A'}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{user ? user.name : S().roles.owner}</div>
                <div style={{ fontSize: 13, color: t.textSec }}>{S().admin.roleLabel} <span style={{ color: t.accentPurple, fontWeight: 700 }}>{user ? ROLE_LABELS[user.role] : S().roles.owner}</span></div>
              </div>
            </div>

            {/* ── Смена личного email (личный аккаунт, не org.email) ── */}
            {globalUser && (
              <div>
                <label style={{ fontSize: 13, color: t.textMuted, marginBottom: 6, display: 'block', fontWeight: 600 }}>
                  {S().emailChange.title || 'Смена email'}
                </label>

                {/* Шаг 0 — показываем текущий email + кнопка «Изменить» */}
                {mailStep === 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <Mail size={16} color={t.textMuted} style={{ position: 'absolute', left: 14, top: 12 }} />
                      <input readOnly value={globalUser.email || ''} style={{ fontSize: 15, padding: '10px 14px 10px 40px', width: '100%', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text, opacity: 0.8 }} />
                    </div>
                    <Btn variant="ghost" onClick={() => { setMailStep(1); setMailError(''); setMailSuccess(''); }}>
                      <Edit3 size={14} /> {S().emailChange.changeBtn || 'Изменить'}
                    </Btn>
                  </div>
                )}

                {/* Шаг 1 — ввод нового email */}
                {/* Чанк F.1: обёрнут в <form> для убирания Chrome warning. */}
                {mailStep === 1 && (
                  <form onSubmit={e => e.preventDefault()} className="anim-tx" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: t.bgInput, borderRadius: 10, border: `1px dashed ${t.accentOrange}` }}>
                    {/* Чанк F.2: hidden username для accessibility. */}
                    <input type="text" name="username" autoComplete="username" defaultValue={globalUser?.login || globalUser?.email || user?.login || ""} readOnly style={{ display: "none" }} aria-hidden="true" />
                    <div style={{ fontSize: 12, color: t.textMuted }}>
                      {S().emailChange.step1Desc || 'Введите новый email. Мы отправим на него код подтверждения.'}
                    </div>
                    <input
                      type="email"
                      autoComplete="email"
                      placeholder={S().emailChange.newEmailPh || 'new@example.com'}
                      value={newMail}
                      onChange={e => setNewMail(e.target.value)}
                      style={{ width: '100%', fontSize: 14, borderRadius: 6, border: `1px solid ${t.border}`, padding: '0 10px', background: t.bgCard, color: t.text, height: 38 }}
                    />
                    {/* Stage 11: текущий пароль обязателен — защита от угона email через краденый access-токен */}
                    <input
                      type="password"
                      autoComplete="current-password"
                      placeholder="Текущий пароль"
                      value={mailCurrentPw}
                      onChange={e => { setMailCurrentPw(e.target.value); setMailError(''); }}
                      style={{ width: '100%', fontSize: 14, borderRadius: 6, border: `1px solid ${t.border}`, padding: '0 10px', background: t.bgCard, color: t.text, height: 38 }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Btn variant="success" onClick={mailRequestCode} disabled={mailLoading || !mailCurrentPw} style={{ flex: 1 }}>
                        {mailLoading ? '...' : (S().emailChange.sendCode || 'Отправить код')}
                      </Btn>
                      <Btn variant="ghost" onClick={mailChangeCancel}><X size={16} /></Btn>
                    </div>
                  </form>
                )}

                {/* Шаг 2 — ввод OTP */}
                {mailStep === 2 && (
                  <div className="anim-tx" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: t.bgInput, borderRadius: 10, border: `1px dashed ${t.accentOrange}` }}>
                    <div style={{ fontSize: 12, color: t.textMuted }}>
                      {S().emailChange.step2Desc || 'Мы отправили 6-значный код на новый адрес. Введите его ниже.'} <span style={{ color: t.text, fontWeight: 600 }}>{newMail}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        inputMode="numeric"
                        maxLength={6}
                        placeholder={S().emailChange.codePh || '123456'}
                        value={mailOtp}
                        onChange={e => setMailOtp(e.target.value.replace(/\D/g, ''))}
                        style={{ flex: 1, fontSize: 14, borderRadius: 6, border: `1px solid ${t.border}`, padding: '0 10px', background: t.bgCard, color: t.text, height: 38, letterSpacing: 2 }}
                      />
                      <Btn variant="success" onClick={mailConfirm} disabled={mailLoading || mailOtp.length !== 6}>
                        {mailLoading ? '...' : (S().emailChange.confirm || 'Подтвердить')}
                      </Btn>
                      <Btn variant="ghost" onClick={mailChangeCancel}><X size={16} /></Btn>
                    </div>
                  </div>
                )}

                {mailError && (
                  <div style={{ fontSize: 12, color: t.accentRed, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AlertCircle size={12} /> {mailError}
                  </div>
                )}
                {mailSuccess && (
                  <div style={{ fontSize: 12, color: t.accentGreen, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle size={12} /> {mailSuccess}
                  </div>
                )}
              </div>
            )}

            {/* ── Личный телефон (globalUser.phone) ── */}
            {globalUser && (
              <div>
                <label style={{ fontSize: 13, color: t.textMuted, marginBottom: 6, display: 'block', fontWeight: 600 }}>
                  {S().admin.myPhoneLabel || 'Мой телефон'}
                </label>

                {!phoneEdit && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <Phone size={16} color={t.textMuted} style={{ position: 'absolute', left: 14, top: 12 }} />
                      <input
                        readOnly
                        value={globalUser.phone ? formatPhone(globalUser.phone) : (S().admin.phoneNotSet || 'Не указан')}
                        style={{ fontSize: 15, padding: '10px 14px 10px 40px', width: '100%', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: globalUser.phone ? t.text : t.textMuted, opacity: 0.8 }}
                      />
                    </div>
                    <Btn variant="ghost" onClick={() => { setPhoneEdit(true); setPhoneErr(''); setPhoneOk(''); setPhoneDraft(phoneDigits(globalUser?.phone || '')); }}>
                      <Edit3 size={14} /> {S().emailChange.changeBtn || 'Изменить'}
                    </Btn>
                  </div>
                )}

                {phoneEdit && (
                  <div className="anim-tx" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: t.bgInput, borderRadius: 10, border: `1px dashed ${t.accentOrange}` }}>
                    <div style={{ fontSize: 12, color: t.textMuted }}>
                      {S().admin.phoneHint || 'Только цифры, минимум 7. Пробелы система расставит автоматически.'}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        inputMode="tel"
                        placeholder={S().admin.phonePlaceholder}
                        value={phoneDraft}
                        onChange={e => setPhoneDraft(phoneDigits(e.target.value))}
                        style={{ flex: 1, fontSize: 14, borderRadius: 6, border: `1px solid ${t.border}`, padding: '0 10px', background: t.bgCard, color: t.text, height: 38 }}
                      />
                      <Btn variant="success" onClick={phoneSave} disabled={phoneLoading || !isValidPhone(phoneDraft)}>
                        {phoneLoading ? '...' : (S().common.save || 'Сохранить')}
                      </Btn>
                      <Btn variant="ghost" onClick={phoneCancel}><X size={16} /></Btn>
                    </div>
                    {phoneDraft && (
                      <div style={{ fontSize: 11, color: t.textMuted }}>
                        {formatPhone(phoneDraft)}
                      </div>
                    )}
                  </div>
                )}

                {phoneErr && (
                  <div style={{ fontSize: 12, color: t.accentRed, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AlertCircle size={12} /> {phoneErr}
                  </div>
                )}
                {phoneOk && (
                  <div style={{ fontSize: 12, color: t.accentGreen, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle size={12} /> {phoneOk}
                  </div>
                )}
              </div>
            )}

            {/* Чанк F.1: обёрнут <div> -> <form> для убирания Chrome warning. */}
            <form onSubmit={e => e.preventDefault()}>
              {/* Чанк F.2: hidden username для accessibility. */}
              <input type="text" name="username" autoComplete="username" defaultValue={globalUser?.login || globalUser?.email || user?.login || ""} readOnly style={{ display: "none" }} aria-hidden="true" />
              <label style={{ fontSize: 13, color: t.textMuted, marginBottom: 6, display: 'block', fontWeight: 600 }}>{S().admin.changePassword}</label>
              {/* Stage 11: текущий пароль обязателен для смены — защита от угона access-токена */}
              {pw && pw.length >= 8 && (
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <Lock size={16} color={t.textMuted} style={{ position: 'absolute', left: 14, top: 12 }} />
                  <input disabled={!isOwner} type="password" autoComplete="current-password" value={currentPw} onChange={e => { setCurrentPw(e.target.value); setPwError(''); }} placeholder="Текущий пароль" style={{ fontSize: 15, padding: '10px 14px 10px 40px', width: '100%', opacity: isOwner ? 1 : 0.6, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text }} />
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <Lock size={16} color={t.textMuted} style={{ position: 'absolute', left: 14, top: 12 }} />
                <input disabled={!isOwner} type="password" autoComplete="new-password" value={pw} onChange={e => setPw(stripCyrillic(e.target.value))} placeholder={S().admin.newPasswordPlaceholder} style={{ fontSize: 15, padding: '10px 14px 10px 40px', width: '100%', opacity: isOwner ? 1 : 0.6, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text }} />
              </div>
              <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6 }}>{S().admin.passwordHint}</div>
              {pw && pw.length > 0 && pw.length < 8 && <div style={{ fontSize: 11, color: t.accentRed, marginTop: 4 }}>{S().setup.passwordMinLength}</div>}
              {pwError && <div style={{ fontSize: 12, color: t.accentRed, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}><AlertCircle size={12} /> {pwError}</div>}
            </form>
          </Card>
        </div>
      </div>

      {/* Кнопка СОХРАНИТЬ — на всю ширину, заметная */}
      {isOwner && (
        <Btn variant="success" onClick={save} style={{
          padding: '16px', fontSize: 16, justifyContent: 'center', width: '100%',
          opacity: hasChanges ? 1 : 0.5,
          transition: 'opacity 0.2s',
        }}>
          <Save size={20} /> {S().admin.saveAll}
        </Btn>
      )}

      {/* Stage 11/Этап 2: SessionsCard и SecurityEventsCard теперь в отдельном табе "Мой аккаунт" (MyAccountTab) */}
    </div>
  );
}

// ══════════════════════ Карточка: МОИ УСТРОЙСТВА (Stage 11) ══════════════════════
// Список активных сессий юзера + возможность завершить конкретную или все.
// Backend: GET /api/auth/sessions, DELETE /api/auth/sessions/:id, POST /api/auth/logout-all.
// Stage 11/Этап 2: экспортирована — переиспользуется в MyAccountTab (для всех ролей).
export function SessionsCard() {
  const t = useTheme();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revokingId, setRevokingId] = useState(null);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);
  // Stage 11: ID текущей сессии — приходит с /login, хранится в localStorage.
  // Меняется при /refresh (rotation), но api.js обновляет его автоматически.
  const currentSessionId = api.getSessionId();

  const load = () => {
    setLoading(true);
    setError('');
    api.getSessions()
      .then(d => setSessions(Array.isArray(d?.sessions) ? d.sessions : []))
      .catch(e => setError(e?.message || S().admin.security.errorLoad))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleRevoke = (id) => {
    if (!window.confirm(S().admin.security.confirmRevoke)) return;
    setRevokingId(id);
    api.revokeSession(id)
      .then(() => setSessions(prev => prev.filter(s => s.id !== id)))
      .catch(e => alert(S().admin.security.errorPrefix + (e?.message || '')))
      .finally(() => setRevokingId(null));
  };

  const handleLogoutAll = () => {
    if (!window.confirm(S().admin.security.confirmLogoutAll)) return;
    setLogoutAllLoading(true);
    api.logoutAll()
      .then(() => {
        api.logout(); // local clearAuth
        window.location.href = '/app/';
      })
      .catch(e => {
        alert(S().admin.security.errorPrefix + (e?.message || ''));
        setLogoutAllLoading(false);
      });
  };

  // Парсинг user-agent через ua-parser-js — даёт OS+version, browser+version,
  // device.type (mobile/tablet/desktop), device.model (iPhone, Galaxy S22, ...).
  // Возвращает { label, type, vendor } для рендера.
  const parseUA = (ua) => {
    const unknownLabel = S().admin.security.unknownDevice;
    if (!ua) return { label: unknownLabel, type: 'unknown', vendor: null };
    try {
      const r = new UAParser(ua).getResult();
      const os = r.os?.name ? `${r.os.name}${r.os.version ? ' ' + r.os.version.split('.')[0] : ''}` : '';
      const br = r.browser?.name ? `${r.browser.name}${r.browser.version ? ' ' + r.browser.version.split('.')[0] : ''}` : '';
      // Модель устройства: iPhone, Galaxy S23, iPad Pro... (только для mobile/tablet — на desktop пусто).
      const model = r.device?.model && r.device?.vendor
        ? `${r.device.vendor} ${r.device.model}`
        : (r.device?.model || '');
      const parts = [model, os, br].filter(Boolean);
      return {
        label: parts.length ? parts.join(' · ') : (ua.slice(0, 60) || unknownLabel),
        type: r.device?.type || 'desktop',
        vendor: r.device?.vendor || r.os?.name || null,
      };
    } catch (e) {
      return { label: String(ua).slice(0, 60), type: 'unknown', vendor: null };
    }
  };

  // Иконка устройства по типу. Lucide-react.
  const DeviceIcon = ({ type, size = 16, color }) => {
    const Icon = type === 'mobile' ? Smartphone
      : type === 'tablet' ? Tablet
      : type === 'desktop' ? Laptop
      : Monitor;
    return <Icon size={size} color={color} />;
  };

  // Формат гео: "Алматы, KZ" / "Москва, RU" / null если нечего показать.
  const fmtGeo = (city, country) => {
    if (city && country) return `${city}, ${country}`;
    if (city) return city;
    if (country) return country;
    return null;
  };

  const fmtDateTime = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString(); } catch (_) { return String(d); }
  };

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Shield size={18} color={t.accent} />
        <div style={{ fontSize: 15, fontWeight: 700, color: t.text }}>{S().admin.security.devicesTitle}</div>
        <Btn variant="ghost" onClick={load} disabled={loading} style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 12 }}>
          {loading ? '...' : S().admin.security.refresh}
        </Btn>
      </div>

      {loading && <div style={{ fontSize: 13, color: t.textMuted }}>{S().admin.security.loading}</div>}

      {!loading && error && (
        <div style={{ fontSize: 13, color: t.accentRed, display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {!loading && !error && sessions.length === 0 && (
        <div style={{ fontSize: 13, color: t.textMuted }}>{S().admin.security.noSessions}</div>
      )}

      {!loading && !error && sessions.length > 0 && (
        <>
          <div style={{ display: 'grid', gap: 8 }}>
            {sessions.map(s => {
              const isCurrent = currentSessionId && s.id === currentSessionId;
              const dev = parseUA(s.userAgent);
              const geo = fmtGeo(s.city, s.country);
              return (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                  padding: '10px 12px', borderRadius: 8,
                  // Подсветка текущей сессии: лёгкий зелёный фон + рамка цвета акцента.
                  background: isCurrent ? `${t.accentGreen}10` : t.bgInput,
                  border: `1px solid ${isCurrent ? t.accentGreen : t.border}`,
                }}>
                  {/* Иконка устройства — даёт быстрое визуальное распознавание */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 8,
                    background: isCurrent ? `${t.accentGreen}20` : `${t.accent}10`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <DeviceIcon type={dev.type} size={18} color={isCurrent ? t.accentGreen : t.accent} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{dev.label}</div>
                      {isCurrent && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                          background: t.accentGreen, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5,
                        }}>
                          {S().admin.security.currentBadge}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {geo && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <MapPin size={11} /> {geo}
                        </span>
                      )}
                      {geo && <span>·</span>}
                      <span>{S().admin.security.ipLabel}: {s.ip || '—'}</span>
                      <span>·</span>
                      <span>{fmtDateTime(s.createdAt)}</span>
                    </div>
                  </div>
                  {isCurrent ? (
                    <div style={{ fontSize: 11, color: t.textMuted, padding: '6px 12px', whiteSpace: 'nowrap' }}>
                      {S().admin.security.thisDevice}
                    </div>
                  ) : (
                    <Btn variant="ghost" disabled={revokingId === s.id} onClick={() => handleRevoke(s.id)}
                      style={{ color: t.accentRed, fontSize: 12, padding: '6px 12px' }}>
                      {revokingId === s.id ? '...' : S().admin.security.terminate}
                    </Btn>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${t.border}` }}>
            <Btn variant="ghost" onClick={handleLogoutAll} disabled={logoutAllLoading}
              style={{ color: t.accentRed, fontSize: 13 }}>
              <Trash2 size={14} /> {logoutAllLoading ? S().admin.security.logoutAllInProgress : S().admin.security.logoutAll}
            </Btn>
            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6 }}>
              {S().admin.security.logoutAllHint}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

// ══════════════════════ Карточка: АКТИВНОСТЬ БЕЗОПАСНОСТИ (Stage 11) ══════════════════════
// Лента последних 50 sensitive-событий юзера: смены пароля/email, 2FA on/off, удаление орг,
// reuse_detected (украденный токен), logout_all, session_revoked.
// Backend: GET /api/me/security-events.
//
// SECURITY: эта лента — главный сигнал инцидента для юзера. reuse_detected рендерится
// красным с явным предупреждением, чтобы юзер немедленно сменил пароль и проверил сессии.
// Stage 11/Этап 2: экспортирована — используется в MyAccountTab для всех ролей.
export function SecurityEventsCard() {
  const t = useTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    api.getSecurityEvents()
      .then(d => setEvents(Array.isArray(d?.events) ? d.events : []))
      .catch(e => setError(e?.message || S().admin.security.errorLoad))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Метаданные по eventType: иконка, цвет, локализованный заголовок (через S()).
  // Расширяемо: при добавлении нового типа — допиши ключ в strings.events.types и иконку ниже.
  const types = S().admin.security.events.types;
  const META = {
    password_changed:  { icon: KeyRound,      color: t.accent,      title: types.password_changed },
    password_reset:    { icon: KeyRound,      color: t.accent,      title: types.password_reset },
    email_changed:     { icon: Mail,          color: t.accent,      title: types.email_changed },
    '2fa_enabled':     { icon: ShieldCheck,   color: t.accentGreen, title: types['2fa_enabled'] },
    '2fa_disabled':    { icon: ShieldOff,     color: t.accentOrange, title: types['2fa_disabled'] },
    org_deleted:       { icon: Trash2,        color: t.accentRed,   title: types.org_deleted },
    account_deleted:   { icon: Trash2,        color: t.accentRed,   title: types.account_deleted },
    reuse_detected:    { icon: AlertTriangle, color: t.accentRed,   title: types.reuse_detected, critical: true },
    logout_all:        { icon: LogOut,        color: t.accent,      title: types.logout_all },
    session_revoked:   { icon: X,             color: t.accent,      title: types.session_revoked },
    admin_revoke:      { icon: AlertTriangle, color: t.accentOrange, title: types.admin_revoke },
    sensitive_action_locked: { icon: AlertTriangle, color: t.accentRed, title: types.sensitive_action_locked, critical: true },
  };

  const fmtDT = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString(); } catch (_) { return String(d); }
  };

  const fmtGeoEvt = (city, country) => {
    if (city && country) return `${city}, ${country}`;
    if (city) return city;
    if (country) return country;
    return null;
  };

  // description приходит как trusted-string с бэка (наш же лог), но из defense-in-depth
  // соображений ограничиваем длину при отображении: длинные описания могут портить UI.
  // React JSX автоматически экранирует — XSS невозможен.
  const truncateDesc = (s) => {
    if (!s) return '';
    const str = String(s);
    return str.length > 120 ? str.slice(0, 120) + '…' : str;
  };

  // Локализованный форматтер description: для известных eventType'ов с известными
  // action-кодами заменяет тех. строку на человекочитаемый текст из strings.
  // Пример: sensitive_action_locked + 'change_password' → 'Смена пароля'.
  // Для остальных — raw description через truncate.
  const actions = S().admin.security.events.actions;
  const formatDescription = (eventType, description) => {
    if (!description) return '';
    if (eventType === 'sensitive_action_locked' && actions[description]) {
      return actions[description];
    }
    return truncateDesc(description);
  };

  // Глобальный сигнал инцидента: есть хотя бы 1 critical event в свежей ленте?
  // Если да — карточка получает красную рамку + warning banner сверху,
  // чтобы юзер увидел угрозу с одного взгляда, не читая каждую строку.
  const hasCritical = events.some(e => META[e.eventType]?.critical);

  return (
    <Card style={hasCritical ? { borderColor: t.accentRed, borderWidth: 2 } : {}}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Activity size={18} color={hasCritical ? t.accentRed : t.accent} />
        <div style={{ fontSize: 15, fontWeight: 700, color: hasCritical ? t.accentRed : t.text }}>
          {S().admin.security.events.title}
        </div>
        <Btn variant="ghost" onClick={load} disabled={loading}
          style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 12 }}>
          {loading ? '...' : S().admin.security.refresh}
        </Btn>
      </div>

      {/* Глобальный warning банер при наличии critical event в ленте */}
      {hasCritical && !loading && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 12px', marginBottom: 12, borderRadius: 8,
          background: `${t.accentRed}15`, border: `1px solid ${t.accentRed}`,
          color: t.accentRed, fontSize: 13, fontWeight: 600,
        }}>
          <AlertTriangle size={16} />
          {S().admin.security.events.warningBanner}
        </div>
      )}

      {loading && <div style={{ fontSize: 13, color: t.textMuted }}>{S().admin.security.loading}</div>}

      {!loading && error && (
        <div style={{ fontSize: 13, color: t.accentRed, display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <div style={{ fontSize: 13, color: t.textMuted }}>{S().admin.security.events.empty}</div>
      )}

      {!loading && !error && events.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          {events.map(e => {
            const meta = META[e.eventType] || { icon: Activity, color: t.textMuted, title: e.eventType };
            const Icon = meta.icon;
            const geo = fmtGeoEvt(e.city, e.country);
            return (
              <div key={e.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 12px', borderRadius: 8,
                // Критические события (reuse_detected) — выделены красной рамкой и фоном
                background: meta.critical ? `${t.accentRed}10` : t.bgInput,
                border: `1px solid ${meta.critical ? t.accentRed : t.border}`,
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: `${meta.color}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={16} color={meta.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: meta.critical ? t.accentRed : t.text }}>
                    {meta.title}
                  </div>
                  {e.description && (
                    <div style={{ fontSize: 12, color: t.textSec, marginTop: 2, wordBreak: 'break-word' }}>
                      {formatDescription(e.eventType, e.description)}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>{fmtDT(e.createdAt)}</span>
                    {geo && (
                      <>
                        <span>·</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <MapPin size={11} /> {geo}
                        </span>
                      </>
                    )}
                    {e.ip && (
                      <>
                        <span>·</span>
                        <span>{S().admin.security.ipLabel}: {e.ip}</span>
                      </>
                    )}
                  </div>
                  {meta.critical && (
                    <div style={{ fontSize: 11, color: t.accentRed, marginTop: 6, fontWeight: 600 }}>
                      {S().admin.security.events.criticalWarning}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <div style={{ fontSize: 11, color: t.textMuted, marginTop: 12, textAlign: 'center' }}>
          {S().admin.security.events.showingLast}
        </div>
      )}
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════
// 2FA (Stage 11 UI)
// Карточка "Двухфакторная аутентификация" + Setup wizard (3 шага) + Disable модалка.
// Backend готов: /2fa/setup-init, /setup-confirm, /disable, /status, /verify.
// Stage 11/Этап 2: экспортирована — переиспользуется в MyAccountTab.
// ════════════════════════════════════════════════════════════════════

export function TwoFactorCard() {
  const t = useTheme();
  const [status, setStatus] = useState(null); // {enabled, enabledAt, backupCodesRemaining}
  const [loading, setLoading] = useState(true);
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [error, setError] = useState('');

  const T = S().admin.security.twoFactor;

  const loadStatus = () => {
    setLoading(true);
    api.status2FA()
      .then(s => { setStatus(s); setError(''); })
      .catch(e => setError(e?.message || T.errorLoad))
      .finally(() => setLoading(false));
  };

  useEffect(loadStatus, []);

  const enabled = !!status?.enabled;
  const remaining = status?.backupCodesRemaining || 0;
  const lowBackup = enabled && remaining > 0 && remaining <= 2;

  return (
    <>
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: enabled ? `${t.accentGreen}20` : `${t.accentOrange || t.accent}20`, color: enabled ? t.accentGreen : (t.accentOrange || t.accent), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {enabled ? <ShieldCheck size={20} /> : <Shield size={20} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: t.text }}>{T.title}</div>
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4, lineHeight: 1.5 }}>{T.subtitle}</div>
          </div>
        </div>

        {error && (
          <div style={{ background: `${t.accentRed}15`, color: t.accentRed, padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
            {T.errorPrefix}{error}
          </div>
        )}

        {loading ? (
          <div style={{ fontSize: 13, color: t.textMuted }}>{T.loading}</div>
        ) : enabled ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6, background: `${t.accentGreen}15`, color: t.accentGreen, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle size={13} /> {T.statusEnabled}
              </span>
              {status?.enabledAt && (
                <span style={{ fontSize: 12, color: t.textMuted }}>
                  {T.enabledSince(fmtDay(new Date(status.enabledAt)))}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: lowBackup ? t.accentRed : t.textMuted, fontWeight: lowBackup ? 700 : 400 }}>
              {T.backupRemaining(remaining)}
              {lowBackup && (
                <div style={{ marginTop: 4, fontWeight: 400 }}>
                  <AlertTriangle size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  {T.backupLowWarning}
                </div>
              )}
            </div>
            <div style={{ marginTop: 4 }}>
              <Btn variant="danger" onClick={() => setDisableOpen(true)} style={{ padding: '6px 14px' }}>
                <ShieldOff size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                {T.disableBtn}
              </Btn>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6, background: `${t.bgInput}`, color: t.textMuted, display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start' }}>
              {T.statusDisabled}
            </span>
            <div>
              <Btn variant="success" onClick={() => setSetupOpen(true)} style={{ padding: '8px 16px' }}>
                <ShieldCheck size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                {T.enableBtn}
              </Btn>
            </div>
          </div>
        )}
      </Card>

      {setupOpen && (
        <Setup2FAModal onClose={() => { setSetupOpen(false); loadStatus(); }} />
      )}
      {disableOpen && (
        <Disable2FAModal onClose={() => { setDisableOpen(false); loadStatus(); }} />
      )}
    </>
  );
}

// Setup wizard — 3 шага:
//   1. QR + manual secret + кнопка "Далее"
//   2. ввод 6-значного кода → /setup-confirm (на успехе показываем backup-коды)
//   3. backup codes (10 штук) + чекбокс "сохранил" + кнопка "Готово"
function Setup2FAModal({ onClose }) {
  const t = useTheme();
  const T = S().admin.security.twoFactor;
  const [step, setStep] = useState(1);
  const [secret, setSecret] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [confirmedSaved, setConfirmedSaved] = useState(false);
  const [copiedTag, setCopiedTag] = useState('');
  // Этап 3.1: re-auth текущим паролем перед сохранением 2FA в БД (защита от
  // включения 2FA через украденный access-токен).
  const [currentPassword, setCurrentPassword] = useState('');

  // Init: при открытии запрашиваем secret + otpauthUrl у бэка.
  useEffect(() => {
    setBusy(true);
    setError('');
    api.setupInit2FA()
      .then(r => { setSecret(r.secret); setOtpauthUrl(r.otpauthUrl); })
      .catch(e => setError(e?.message || T.errorInit))
      .finally(() => setBusy(false));
  }, []);

  const copyToClipboard = (text, tag) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedTag(tag);
      setTimeout(() => setCopiedTag(''), 1500);
    });
  };

  const verify = async () => {
    setBusy(true); setError('');
    try {
      // Этап 3.1: передаём currentPassword — бэк проверит ДО сохранения totpSecret.
      const r = await api.setupConfirm2FA(secret, code.trim(), currentPassword);
      // Очищаем пароль из state сразу после успеха (не оставляем в памяти).
      setCurrentPassword('');
      setBackupCodes(Array.isArray(r.backupCodes) ? r.backupCodes : []);
      setStep(3);
    } catch (e) {
      // Различаем ошибки текущего пароля vs неверного TOTP-кода
      if (e?.code === 'CURRENT_PASSWORD_INVALID') {
        setError(T.currentPasswordInvalid || 'Неверный текущий пароль');
      } else if (e?.code === 'CURRENT_PASSWORD_REQUIRED') {
        setError(S().admin.currentPasswordRequired || 'Введите текущий пароль');
      } else {
        setError(e?.message || T.codeInvalid);
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadBackupCodes = () => {
    const text = `INMONEX — 2FA backup codes\n\n${backupCodes.join('\n')}\n\n${T.step3Notice}\n`;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inmonex-2fa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Защита от случайного закрытия на шаге 3 (юзер должен явно подтвердить).
  // На шагах 1-2 закрытие = отмена настройки (totpSecret в БД ещё не сохранён).
  const closeGuarded = () => {
    if (step === 3 && !confirmedSaved) return; // блокируем
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={closeGuarded}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, padding: 24, width: 480, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: t.text }}>{T.setupTitle}</div>
          {step !== 3 && (
            <button onClick={onClose} style={{ background: 'transparent', color: t.textMuted, padding: 4, border: 'none', cursor: 'pointer' }}><X size={20} /></button>
          )}
        </div>

        {error && (
          <div style={{ background: `${t.accentRed}15`, color: t.accentRed, padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{error}</div>
        )}

        {/* ─── STEP 1: QR код + secret вручную ─── */}
        {step === 1 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 6 }}>{T.step1Title}</div>
            <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 14, lineHeight: 1.5 }}>{T.step1Desc}</div>

            {busy ? (
              <div style={{ fontSize: 13, color: t.textMuted, padding: '40px 0', textAlign: 'center' }}>{T.loading}</div>
            ) : otpauthUrl ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
                  <div style={{ background: '#fff', padding: 14, borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,.1)' }}>
                    <QRCodeSVG value={otpauthUrl} size={180} level="M" />
                  </div>
                </div>

                <details style={{ marginBottom: 16 }}>
                  <summary style={{ fontSize: 13, color: t.accent, cursor: 'pointer', userSelect: 'none' }}>{T.manualSecretLabel}</summary>
                  <div style={{ marginTop: 10, padding: 12, background: t.bgInput, borderRadius: 10 }}>
                    <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 6 }}>{T.manualSecretHint}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ flex: 1, fontFamily: 'monospace', fontSize: 13, fontWeight: 700, letterSpacing: 1, wordBreak: 'break-all' }}>{secret}</code>
                      <button onClick={() => copyToClipboard(secret, 'secret')} style={{ background: t.accent, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Copy size={12} /> {copiedTag === 'secret' ? T.copied : T.copyBtn}
                      </button>
                    </div>
                  </div>
                </details>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn onClick={onClose}>{T.cancelBtn}</Btn>
                  <Btn variant="success" onClick={() => setStep(2)}>{T.nextBtn} →</Btn>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* ─── STEP 2: ввод 6-значного кода + текущий пароль ─── */}
        {step === 2 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 6 }}>{T.step2Title}</div>
            <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 16, lineHeight: 1.5 }}>{T.step2Desc}</div>

            <input
              type="text"
              inputMode="numeric"
              autoFocus
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={T.codeInputPh}
              style={{ width: '100%', padding: '14px 16px', fontSize: 22, textAlign: 'center', letterSpacing: 8, fontFamily: 'monospace', fontWeight: 700, marginBottom: 12 }}
            />

            {/* Этап 3.1: re-auth текущим паролем — защита от включения 2FA через
                украденный access-токен. Без strip-cyrillic (это verification-поле). */}
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && code.length === 6 && currentPassword && !busy) verify(); }}
              placeholder={S().admin.currentPasswordPh || 'Текущий пароль'}
              style={{ width: '100%', padding: '12px 14px', fontSize: 14, marginBottom: 16 }}
            />

            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
              <Btn onClick={() => { setStep(1); setError(''); setCurrentPassword(''); }}>{T.backBtn}</Btn>
              <Btn variant="success" onClick={verify} disabled={busy || code.length !== 6 || !currentPassword}>
                {busy ? T.verifying : T.verifyBtn}
              </Btn>
            </div>
          </div>
        )}

        {/* ─── STEP 3: backup codes (показываются ОДИН раз) ─── */}
        {step === 3 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 6 }}>{T.step3Title}</div>
            <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 12, lineHeight: 1.5 }}>{T.step3Desc}</div>

            <div style={{ background: `${t.accentOrange || t.accentRed}15`, border: `1px solid ${t.accentOrange || t.accentRed}40`, color: t.accentRed, padding: 12, borderRadius: 10, fontSize: 12, marginBottom: 14, lineHeight: 1.5 }}>
              {T.step3Notice}
            </div>

            <div style={{ background: t.bgInput, padding: 14, borderRadius: 10, marginBottom: 14, fontFamily: 'monospace', fontSize: 14, fontWeight: 700 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 18px' }}>
                {backupCodes.map((code, i) => (
                  <div key={i} style={{ letterSpacing: 1 }}>{i + 1}. {code}</div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <Btn onClick={() => copyToClipboard(backupCodes.join('\n'), 'codes')}>
                <Copy size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                {copiedTag === 'codes' ? T.copied : T.copyAllBtn}
              </Btn>
              <Btn onClick={downloadBackupCodes}>
                <Download size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                {T.downloadBtn}
              </Btn>
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, color: t.text, marginBottom: 16 }}>
              <input type="checkbox" checked={confirmedSaved} onChange={e => setConfirmedSaved(e.target.checked)} style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
              <span>{T.confirmSavedLabel}</span>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Btn variant="success" onClick={onClose} disabled={!confirmedSaved}>
                {T.finishBtn}
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Disable модалка: пароль + код (TOTP или backup) → /2fa/disable.
// На бэке также проверяется что 2FA включена; UI блокирует кнопку
// при неполных данных и показывает ошибки.
function Disable2FAModal({ onClose }) {
  const t = useTheme();
  const T = S().admin.security.twoFactor;
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await api.disable2FA(password, code.trim());
      setConfirmed(true);
      // Лёгкая пауза чтобы юзер увидел "успех", потом закрытие
      setTimeout(() => onClose(), 900);
    } catch (e) {
      setError(e?.message || '');
      setBusy(false);
    }
  };

  // Аудит-фикс: раньше было `code.trim().length >= 6` — пропускало невалидные длины (7, 10).
  // Используем те же форматы что бэк (auth.js: isTotpCodeFormat / isBackupCodeFormat).
  const trimmedCode = code.trim();
  const isTotp = /^\d{6}$/.test(trimmedCode);
  const isBackup = /^[A-F0-9]{4}-?[A-F0-9]{4}$/i.test(trimmedCode);
  const canSubmit = password.length > 0 && (isTotp || isBackup) && !busy && !confirmed;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={busy ? undefined : onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, padding: 24, width: 440, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: t.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldOff size={18} color={t.accentRed} />
            {T.disableTitle}
          </div>
          {!busy && <button onClick={onClose} style={{ background: 'transparent', color: t.textMuted, padding: 4, border: 'none', cursor: 'pointer' }}><X size={20} /></button>}
        </div>

        <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 14, lineHeight: 1.5 }}>{T.disableDesc}</div>

        {confirmed ? (
          <div style={{ background: `${t.accentGreen}15`, color: t.accentGreen, padding: 14, borderRadius: 10, fontSize: 14, fontWeight: 700, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <CheckCircle size={18} /> {T.disableSuccess}
          </div>
        ) : (
          <>
            {error && (
              <div style={{ background: `${t.accentRed}15`, color: t.accentRed, padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>
            )}

            <input
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={T.passwordPh}
              style={{ width: '100%', padding: '12px 14px', fontSize: 14, marginBottom: 10 }}
            />
            <input
              type="text"
              inputMode="text"
              autoComplete="off"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
              onKeyDown={e => { if (e.key === 'Enter' && canSubmit) submit(); }}
              placeholder={T.codeOrBackupPh}
              style={{ width: '100%', padding: '12px 14px', fontSize: 14, fontFamily: 'monospace', letterSpacing: 1, marginBottom: 14 }}
            />

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn onClick={onClose} disabled={busy}>{T.cancelBtn}</Btn>
              <Btn variant="danger" onClick={submit} disabled={!canSubmit}>
                {busy ? T.disablingBtn : T.disableConfirmBtn}
              </Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════ Вкладка: ПЕРСОНАЛ ══════════════════════
function AUsers({ data, uOrg, user, isOwner, isCompact, meta, uMeta, globalUser, setGlobalUser, orgId }) {
  const t = useTheme();
  const [showAdd, setShowAdd] = useState(false);
  const [addMode, setAddMode] = useState('new'); // 'new' | 'existing'
  const [nu, setNu] = useState({ login: '', password: '', name: '', role: 'accountant', permissions: {} });
  const [eid, setEid] = useState(null);
  const [eu, setEu] = useState({ login: '', name: '', role: '', permissions: {} });
  const [editOrgs, setEditOrgs] = useState([]); // orgs пользователя при редактировании
  const [editOrgLoading, setEditOrgLoading] = useState(false);
  const [newPw, setNewPw] = useState(''); // для сброса пароля
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  // Stage 11: модалка смены логина sub-user-а (только для owner). null = закрыта.
  const [loginModalUser, setLoginModalUser] = useState(null);
  // Этап 2 v2: модалка смены email sub-user-а (только для owner)
  const [emailModalUser, setEmailModalUser] = useState(null);

  // Поиск существующего пользователя
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null); // null | { found, user, ... }
  const [searchLoading, setSearchLoading] = useState(false);
  const [existingRole, setExistingRole] = useState('accountant');
  const [existingPerms, setExistingPerms] = useState({});

  // Фильтр списка сотрудников (по имени / логину / email / телефону)
  const [filterQuery, setFilterQuery] = useState('');

  // Загружаем пользователей с сервера
  const [serverUsers, setServerUsers] = useState([]);
  useEffect(() => {
    if (orgId && api.getToken()) {
      api.getOrgUsers(orgId).then(users => {
        if (Array.isArray(users)) setServerUsers(users);
      }).catch(() => {});
    }
  }, [orgId]);

  const refreshUsers = () => {
    api.getOrgUsers(orgId).then(users => { if (Array.isArray(users)) setServerUsers(users); }).catch(() => {});
  };

  // Fallback на data.users если сервер не ответил
  const allDisplayUsers = serverUsers.length > 0 ? serverUsers : (data.users || []);

  // Применяем фильтр: по имени/логину/email (case-insensitive) или по цифрам телефона (substring)
  const displayUsers = (() => {
    const q = filterQuery.trim();
    if (!q) return allDisplayUsers;
    const qLower = q.toLowerCase();
    const qDigits = q.replace(/\D/g, '');
    return allDisplayUsers.filter(u => {
      if (u.name && u.name.toLowerCase().includes(qLower)) return true;
      if (u.login && u.login.toLowerCase().includes(qLower)) return true;
      if (u.email && u.email.toLowerCase().includes(qLower)) return true;
      if (qDigits && u.phone && phoneDigits(u.phone).includes(qDigits)) return true;
      return false;
    });
  })();
  const metaOrgs = meta?.orgs || [];
  const ownerOrgAccess = globalUser?.orgAccess || [];
  const hasMultipleOrgs = ownerOrgAccess.length > 1;
  // Merge org names from meta with orgAccess
  const allOrgs = ownerOrgAccess.map(oa => {
    const mo = metaOrgs.find(m => m.id === oa.orgId);
    return { ...oa, name: mo?.name || oa.orgId.slice(0, 8), color: mo?.color };
  });

  const [addLoading, setAddLoading] = useState(false);
  const [addErr, setAddErr] = useState('');

  // Поиск пользователя по логину/email
  const doSearch = async () => {
    const q = searchQuery.trim();
    if (q.length < 2) return;
    setSearchLoading(true);
    setSearchResult(null);
    setAddErr('');
    try {
      const result = await api.searchUser(orgId, q);
      setSearchResult(result);
      if (result.found && !result.alreadyInOrg && result.suggestedRole) {
        setExistingRole(result.suggestedRole);
      }
    } catch (err) {
      setAddErr(err.message || 'Ошибка поиска');
    } finally {
      setSearchLoading(false);
    }
  };

  // Добавить существующего пользователя в org
  const addExisting = async () => {
    if (!searchResult || !searchResult.found || searchResult.alreadyInOrg) return;
    setAddLoading(true);
    setAddErr('');
    try {
      const orgUser = await api.addUserToOrg(orgId, {
        login: searchResult.user.login,
        role: existingRole,
        permissions: existingPerms,
      });
      console.log('[ADMIN] Existing user added to org:', orgUser);

      addAuditLog(uOrg, user, {
        type: 'user_add_existing',
        desc: `Пользователь "${searchResult.user.name}" добавлен в организацию`,
        details: `Логин: ${searchResult.user.login}. Роль: ${existingRole}.`
      });

      setSearchQuery('');
      setSearchResult(null);
      setExistingRole('accountant');
      setExistingPerms({});
      setShowAdd(false);
      refreshUsers();
    } catch (err) {
      console.error('[ADMIN] Add existing user failed:', err);
      setAddErr(err.message || 'Ошибка при добавлении пользователя');
    } finally {
      setAddLoading(false);
    }
  };

  // Создать нового пользователя
  const add = async () => {
    if (!nu.login || !nu.password || !nu.name || nu.password.length < 8) return;
    if (displayUsers.some(u => u.login && u.login.toLowerCase() === nu.login.trim().toLowerCase())) {
      setAddErr(S().admin.dupLogin || 'Пользователь с таким логином уже есть в этой организации');
      return;
    }
    setAddLoading(true);
    setAddErr('');

    try {
      // 1. Регистрируем пользователя на сервере (без смены текущей сессии)
      try {
        await api.registerUser(nu.login, nu.password, nu.name);
      } catch (regErr) {
        if (regErr.status === 409 || (regErr.message && regErr.message.includes('уже существует'))) {
          console.log('[ADMIN] User already exists, adding to org...');
        } else {
          throw regErr;
        }
      }

      // 2. Добавляем пользователя в организацию на сервере
      const orgUser = await api.addUserToOrg(orgId, {
        login: nu.login,
        role: nu.role,
        permissions: nu.permissions || {},
      });
      console.log('[ADMIN] User added to org:', orgUser);

      addAuditLog(uOrg, user, {
        type: 'user_create',
        desc: `Создан новый пользователь: ${nu.name}`,
        details: `Логин: ${nu.login}. Роль: ${nu.role}.`
      });

      setNu({ login: '', password: '', name: '', role: 'accountant', permissions: {} });
      setShowAdd(false);
      refreshUsers();
    } catch (err) {
      console.error('[ADMIN] Add user failed:', err);
      setAddErr(err.message || 'Ошибка при добавлении пользователя');
    } finally {
      setAddLoading(false);
    }
  };

  const del = id => {
    if (displayUsers.length <= 1) return;
    if (!window.confirm(S().admin.deleteEmployeeConfirm)) return;
    const target = displayUsers.find(u => u.id === id);

    // Удаляем с сервера
    api.removeUser(orgId, id).then(() => {
      console.log('[ADMIN] User removed from org on server');
      refreshUsers();
    }).catch(err => {
      console.error('[ADMIN] Remove user failed:', err);
      alert(err.message || S().common.deleteError);
    });

    uOrg(p => ({ ...p, users: p.users.filter(u => u.id !== id) }));

    addAuditLog(uOrg, user, {
      type: 'user_delete',
      desc: `Удален пользователь: ${target?.name || id}`,
      details: `Логин: ${target?.login}. Роль: ${target?.role}`
    });
  };

  const startEdit = (u) => {
    setEid(u.id);
    setEu({ login: u.login, name: u.name, role: u.role, permissions: u.permissions || {} });
    setNewPw('');
    setPwMsg('');
    setEditOrgs([]);
    // Загружаем org-доступы пользователя
    if (hasMultipleOrgs) {
      setEditOrgLoading(true);
      api.getUserOrgs(orgId, u.id).then(orgs => {
        setEditOrgs(orgs || []);
      }).catch(() => {}).finally(() => setEditOrgLoading(false));
    }
  };

  const doResetPw = async (userId) => {
    if (!newPw || newPw.length < 8) return;
    // Stage 11: явное подтверждение перед сбросом — backend сейчас сбрасывает не только
    // пароль, но и 2FA + все активные сессии (force-logout). Случайный клик — слишком дорого.
    if (!window.confirm(S().admin.resetPasswordWarning)) return;
    setPwSaving(true);
    setPwMsg('');
    try {
      await api.resetUserPassword(orgId, userId, newPw);
      setPwMsg(S().admin.passwordResetSuccess);
      setNewPw('');
      // После full reset обновляем список юзеров — потенциально изменилось состояние 2FA и т.п.
      refreshUsers();
    } catch (err) {
      setPwMsg(err.message || 'Ошибка');
    } finally {
      setPwSaving(false);
    }
  };

  const toggleOrgAccess = async (userId, targetOrgId, currentlyEnabled) => {
    setEditOrgLoading(true);
    try {
      const updated = await api.toggleUserOrg(orgId, userId, targetOrgId, !currentlyEnabled, eu.role || 'viewer');
      if (Array.isArray(updated)) setEditOrgs(updated);
    } catch (err) {
      alert(err.message || S().common.genericError);
    } finally {
      setEditOrgLoading(false);
    }
  };

  const saveE = () => {
    if (!eu.name) return;
    const old = displayUsers.find(u => u.id === eid);

    // Обновляем на сервере
    api.updateUserRole(orgId, eid, eu.role, eu.permissions || {}).then(() => {
      console.log('[ADMIN] User role updated on server');
      refreshUsers();
    }).catch(err => {
      console.error('[ADMIN] Update user role failed:', err);
    });

    uOrg(p => ({ ...p, users: p.users.map(u => u.id === eid ? { ...u, ...eu } : u) }));

    addAuditLog(uOrg, user, {
      type: 'user_edit',
      desc: `Изменены данные пользователя: ${eu.name}`,
      details: `Роль: ${old?.role} -> ${eu.role}. Логин: ${eu.login}.`
    });

    setEid(null);
  };

  const roleOptions = (
    <>
      <option value="owner">{S().roles.owner}</option>
      <option value="admin">{S().roles.admin}</option>
      <option value="accountant">{S().roles.accountant}</option>
      <option value="viewer">{S().roles.viewer}</option>
    </>
  );

  const PermToggles = ({ val, set, role }) => {
    // Владелец имеет полный доступ — переключатели не нужны
    if (role === 'owner') return null;
    const base = ROLE_PERMS[role] || {};
    return (
      <div style={{ marginTop: 4, padding: '16px 18px', background: `${t.accent}08`, borderRadius: 12, border: `1px solid ${t.border}` }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: t.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{S().admin.permOverrides}</div>
        <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
          {Object.entries(PERM_LABELS).map(([k, label]) => {
            const isSet = val[k] !== undefined ? val[k] : !!base[k];
            const isManual = val[k] !== undefined;
            return (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 10px', borderRadius: 8, background: isManual ? `${t.accent}15` : 'transparent', transition: 'background .15s' }}>
                <input 
                  type="checkbox" 
                  checked={isSet} 
                  onChange={e => set({ ...val, [k]: e.target.checked })}
                  style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span style={{ fontSize: 13, color: isSet ? t.text : t.textMuted, fontWeight: isManual ? 700 : 400, whiteSpace: 'nowrap' }}>{label}</span>
                  <HelpTooltip text={HELP_TEXTS[`perm_${k}`]} size={13} />
                </div>
              </label>
            );
          })}
        </div>
        <div style={{ fontSize: 10, color: t.textMuted, marginTop: 8 }}>{S().admin.permHint}</div>
      </div>
    );
  };

  // Auto-remove overflow:hidden after animation finishes
  const addFormRef = useRef(null);
  useEffect(() => {
    if (!showAdd || !addFormRef.current) return;
    const el = addFormRef.current;
    const handler = () => el.classList.add('anim-done');
    el.addEventListener('animationend', handler);
    return () => el.removeEventListener('animationend', handler);
  }, [showAdd]);

  return (
    <>
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{S().admin.employees(allDisplayUsers.length)}</div>
          {!isOwner && <div style={{ fontSize: 12, color: t.accentRed, marginTop: 4 }}>{S().admin.viewOnlyHint}</div>}
        </div>
        {isOwner && <Btn onClick={() => setShowAdd(!showAdd)}><Plus size={15}/>{S().admin.addEmployee}</Btn>}
      </div>

      {/* Фильтр списка сотрудников: по имени/логину/email или по цифрам телефона */}
      <div style={{ marginBottom: 14, position: 'relative' }}>
        <Search size={16} color={t.textMuted} style={{ position: 'absolute', left: 12, top: 11 }} />
        <input
          value={filterQuery}
          onChange={e => setFilterQuery(e.target.value)}
          placeholder={S().admin.filterUsersPlaceholder || 'Поиск: имя, логин, email или цифры телефона'}
          style={{ width: '100%', padding: '9px 14px 9px 36px', borderRadius: 10, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text, fontSize: 14 }}
        />
        {filterQuery && (
          <button onClick={() => setFilterQuery('')} style={{ position: 'absolute', right: 8, top: 8, background: 'transparent', border: 'none', color: t.textMuted, cursor: 'pointer', padding: 4 }}>
            <X size={14} />
          </button>
        )}
      </div>

      {showAdd && isOwner && (
        <div ref={addFormRef} className="anim-tx" style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20, padding: 20, background: t.bgInput, borderRadius: 14, border: `1px dashed ${t.accent}` }}>
          {/* Заголовок + переключатель режима */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => { setAddMode('existing'); setAddErr(''); }} style={{ fontSize: 13, fontWeight: addMode === 'existing' ? 800 : 500, padding: '7px 14px', borderRadius: 8, border: `1px solid ${addMode === 'existing' ? t.accent : t.border}`, background: addMode === 'existing' ? `${t.accent}15` : 'transparent', color: addMode === 'existing' ? t.accent : t.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Search size={14} /> {S().admin.addExisting}
              </button>
              <button onClick={() => { setAddMode('new'); setAddErr(''); }} style={{ fontSize: 13, fontWeight: addMode === 'new' ? 800 : 500, padding: '7px 14px', borderRadius: 8, border: `1px solid ${addMode === 'new' ? t.accent : t.border}`, background: addMode === 'new' ? `${t.accent}15` : 'transparent', color: addMode === 'new' ? t.accent : t.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <UserPlus size={14} /> {S().admin.addNew}
              </button>
            </div>
            <button onClick={() => { setShowAdd(false); setAddErr(''); setSearchResult(null); setSearchQuery(''); }} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}><X size={18} /></button>
          </div>

          {/* === Режим: Добавить существующего === */}
          {addMode === 'existing' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  placeholder={S().admin.searchUserPlaceholder}
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setSearchResult(null); }}
                  onKeyDown={e => e.key === 'Enter' && doSearch()}
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, fontSize: 14 }}
                />
                <Btn onClick={doSearch} disabled={searchLoading || searchQuery.trim().length < 2} style={{ padding: '10px 16px', opacity: searchLoading ? 0.6 : 1 }}>
                  <Search size={15} /> {searchLoading ? S().admin.searching : S().admin.searchBtn}
                </Btn>
              </div>

              {/* Результат поиска */}
              {searchResult && !searchResult.found && (
                <div style={{ padding: '12px 16px', background: `${t.accentOrange}10`, borderRadius: 10, border: `1px solid ${t.accentOrange}30`, color: t.accentOrange, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertCircle size={16} /> {S().admin.userNotFound}
                </div>
              )}

              {searchResult && searchResult.found && searchResult.alreadyInOrg && (
                <div style={{ padding: '12px 16px', background: `${t.accentOrange}10`, borderRadius: 10, border: `1px solid ${t.accentOrange}30`, color: t.accentOrange, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertCircle size={16} /> {S().admin.userAlreadyInOrg}
                </div>
              )}

              {searchResult && searchResult.found && !searchResult.alreadyInOrg && (
                <div style={{ padding: 16, background: `${t.accentGreen}08`, borderRadius: 12, border: `1px solid ${t.accentGreen}30`, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: `${t.accentGreen}20`, color: t.accentGreen, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800 }}>
                      {searchResult.user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: t.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CheckCircle size={16} color={t.accentGreen} /> {searchResult.user.name}
                      </div>
                      <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>
                        {searchResult.user.login}
                        {searchResult.user.maskedEmail && <span style={{ marginLeft: 8 }}>{searchResult.user.maskedEmail}</span>}
                        {searchResult.currentOrgs > 0 && <span style={{ marginLeft: 8, color: t.accent }}>{S().admin.userOrgsCount(searchResult.currentOrgs)}</span>}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().admin.roleSelectLabel}</label>
                      <select value={existingRole} onChange={e => setExistingRole(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, fontSize: 14 }}>
                        {roleOptions}
                      </select>
                      {searchResult.suggestedRole && (
                        <div style={{ fontSize: 10, color: t.textMuted, marginTop: 4 }}>{S().admin.suggestedRole}: {ROLE_LABELS[searchResult.suggestedRole] || searchResult.suggestedRole}</div>
                      )}
                    </div>
                  </div>

                  <PermToggles val={existingPerms} set={setExistingPerms} role={existingRole} />

                  <div style={{ fontSize: 11, color: t.textMuted }}>{S().admin.addToOrgHint}</div>
                </div>
              )}

              {/* Ошибка */}
              {addErr && <div style={{ color: t.accentRed, fontSize: 13, padding: '8px 12px', background: `${t.accentRed}10`, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6 }}><AlertCircle size={14} /> {addErr}</div>}

              {/* Кнопки */}
              {searchResult && searchResult.found && !searchResult.alreadyInOrg && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <Btn variant="ghost" onClick={() => { setShowAdd(false); setAddErr(''); setSearchResult(null); setSearchQuery(''); }} style={{ padding: '10px 18px' }}><X size={16} /> {S().common.cancel}</Btn>
                  <Btn variant="success" onClick={addExisting} disabled={addLoading} style={{ padding: '10px 22px', opacity: addLoading ? 0.6 : 1 }}><Check size={18} /> {addLoading ? '...' : S().admin.addToOrg}</Btn>
                </div>
              )}
            </div>
          )}

          {/* === Режим: Создать нового === */}
          {addMode === 'new' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'repeat(2, 1fr)', gap: 12 }}>
                <div><label style={{fontSize:11, color:t.textMuted, fontWeight:700, marginLeft:4, marginBottom: 4, display: 'block'}}>{S().admin.employeeName}</label><input placeholder={S().admin.employeeNamePlaceholder} value={nu.name} onChange={e => setNu({ ...nu, name: e.target.value })} style={{width:'100%', padding:'10px 14px', borderRadius:10, border:`1px solid ${t.border}`, background:t.bgCard, color:t.text, fontSize: 14}}/></div>
                <div><label style={{fontSize:11, color:t.textMuted, fontWeight:700, marginLeft:4, marginBottom: 4, display: 'block'}}>{S().admin.roleSelectLabel}</label><select value={nu.role} onChange={e => setNu({ ...nu, role: e.target.value })} style={{width:'100%', padding:'10px 14px', borderRadius:10, border:`1px solid ${t.border}`, background:t.bgCard, color:t.text, fontSize: 14}}>{roleOptions}</select></div>
                <div><label style={{fontSize:11, color:t.textMuted, fontWeight:700, marginLeft:4, marginBottom: 4, display: 'block'}}>{S().admin.loginLabel}</label><input placeholder={S().admin.loginPlaceholder} value={nu.login} onChange={e => setNu({ ...nu, login: e.target.value })} style={{width:'100%', padding:'10px 14px', borderRadius:10, border:`1px solid ${t.border}`, background:t.bgCard, color:t.text, fontSize: 14}}/></div>
                <div><label style={{fontSize:11, color:t.textMuted, fontWeight:700, marginLeft:4, marginBottom: 4, display: 'block'}}>{S().admin.initialPassword}</label><input type="text" placeholder={S().admin.initialPasswordPlaceholder} value={nu.password} onChange={e => setNu({ ...nu, password: stripCyrillic(e.target.value) })} style={{width:'100%', padding:'10px 14px', borderRadius:10, border:`1px solid ${t.border}`, background:t.bgCard, color:t.text, fontSize: 14}}/>{nu.password && nu.password.length > 0 && nu.password.length < 8 && <div style={{ fontSize: 11, color: t.accentRed, marginTop: 4 }}>{S().setup.passwordMinLength}</div>}</div>
              </div>

              <PermToggles val={nu.permissions} set={p => setNu({ ...nu, permissions: p })} role={nu.role} />

              {addErr && <div style={{ color: t.accentRed, fontSize: 13, padding: '8px 12px', background: `${t.accentRed}10`, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6 }}><AlertCircle size={14} /> {addErr}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
                <Btn variant="ghost" onClick={() => { setShowAdd(false); setAddErr(''); }} style={{ padding: '10px 18px' }}><X size={16} /> {S().common.cancel}</Btn>
                <Btn variant="success" onClick={add} disabled={addLoading} style={{ padding: '10px 22px', opacity: addLoading ? 0.6 : 1 }}><Check size={18}/> {addLoading ? '...' : S().admin.addEmployee}</Btn>
              </div>
            </>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {displayUsers.map(u => {
          const isE = eid === u.id;
          const isOwn = u.role === 'owner';
          const badgeColor = isOwn ? t.accentPurple : u.role === 'admin' ? t.accentYellow : u.role === 'accountant' ? t.accentGreen : t.textMuted;
          const label = isOwn ? S().roles.owner : u.role === 'admin' ? S().roles.admin : ROLE_LABELS[u.role] || S().roles.viewer;

          return (
            <div key={u.id} style={{ padding: '14px 16px', borderRadius: 12, background: t.bgInput, border: `1px solid ${t.border}` }}>
              {isE ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : 'repeat(2, 1fr)', gap: 12 }}>
                    <div><div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4, fontWeight:700 }}>{S().admin.employeeName}</div><input value={eu.name} onChange={e => setEu({ ...eu, name: e.target.value })} style={{ width: '100%', fontSize: 14, padding:'10px 14px', borderRadius:10, border:`1px solid ${t.border}`, background:t.bgCard, color:t.text }} /></div>
                    <div><div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4, fontWeight:700 }}>{S().admin.roleSelectLabel}</div><select value={eu.role} onChange={e => setEu({ ...eu, role: e.target.value })} style={{ width: '100%', fontSize: 14, padding:'10px 14px', borderRadius:10, border:`1px solid ${t.border}`, background:t.bgCard, color:t.text }}>{roleOptions}</select></div>
                    <div>
                      <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4, fontWeight:700 }}>{S().admin.loginDisplayLabel}</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input value={eu.login} disabled style={{ flex: 1, fontSize: 14, padding:'10px 14px', borderRadius:10, border:`1px solid ${t.border}`, background:t.bgCard, color:t.textMuted, opacity: 0.7 }} />
                        {/* Stage 11: смена логина sub-user-а — только owner, не для самого owner-а */}
                        {isOwner && !isOwn && (
                          <Btn onClick={() => setLoginModalUser({ id: u.id, login: u.login, name: u.name })} style={{ padding: '8px 12px' }} title={S().admin.changeLogin}>
                            <Edit3 size={14} />
                          </Btn>
                        )}
                      </div>
                    </div>
                    {/* Этап 2 v2: email сотрудника — критично при увольнении (см. ChangeEmailModal). */}
                    {isOwner && !isOwn && (
                      <div>
                        <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4, fontWeight:700 }}>{S().emailChange.title || 'Email'}</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input
                            value={u.email || ''}
                            disabled
                            placeholder={S().admin.emailNotSet || 'не указан'}
                            style={{ flex: 1, fontSize: 14, padding:'10px 14px', borderRadius:10, border:`1px solid ${t.border}`, background:t.bgCard, color: u.email ? t.textMuted : t.textMuted, opacity: 0.7, fontStyle: u.email ? 'normal' : 'italic' }}
                          />
                          <Btn onClick={() => setEmailModalUser({ id: u.id, email: u.email || '', name: u.name })} style={{ padding: '8px 12px' }} title={S().admin.changeEmail}>
                            <Mail size={14} />
                          </Btn>
                        </div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4, fontWeight:700 }}>{S().admin.resetPassword}</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input type="text" placeholder={S().admin.resetPasswordPlaceholder} value={newPw} onChange={e => setNewPw(stripCyrillic(e.target.value))} style={{ flex: 1, fontSize: 14, padding:'10px 14px', borderRadius:10, border:`1px solid ${t.border}`, background:t.bgCard, color:t.text }} />
                        <Btn onClick={() => doResetPw(u.id)} disabled={pwSaving || newPw.length < 8} style={{ padding: '8px 12px', opacity: pwSaving ? 0.6 : 1 }}>
                          <KeyRound size={14} />
                        </Btn>
                      </div>
                      {newPw && newPw.length > 0 && newPw.length < 8 && <div style={{ fontSize: 11, color: t.accentRed, marginTop: 4 }}>{S().setup.passwordMinLength}</div>}
                      {pwMsg && <div style={{ fontSize: 11, marginTop: 4, color: pwMsg === S().admin.passwordResetSuccess ? t.accentGreen : t.accentRed, fontWeight: 600 }}>{pwMsg}</div>}
                    </div>
                  </div>

                  <PermToggles val={eu.permissions || {}} set={p => setEu({ ...eu, permissions: p })} role={eu.role} />

                  {/* Привязка к организациям — только если есть больше 1 org */}
                  {hasMultipleOrgs && (
                    <div style={{ padding: '14px 16px', background: `${t.accent}08`, borderRadius: 12, border: `1px solid ${t.border}` }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: t.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Building2 size={14} /> {S().admin.orgAssignment}
                      </div>
                      {editOrgLoading ? (
                        <div style={{ fontSize: 13, color: t.textMuted, padding: 8 }}>...</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {allOrgs.map(oa => {
                            const isInOrg = editOrgs.some(eo => eo.orgId === oa.orgId);
                            const orgAccess = editOrgs.find(eo => eo.orgId === oa.orgId);
                            const isCurrent = oa.orgId === orgId;
                            const isOrgOwner = orgAccess && orgAccess.role === 'owner';
                            return (
                              <label key={oa.orgId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: isInOrg ? `${t.accent}10` : 'transparent', cursor: isOrgOwner ? 'default' : 'pointer', border: `1px solid ${isCurrent ? t.accent + '40' : 'transparent'}` }}>
                                <input
                                  type="checkbox"
                                  checked={isInOrg}
                                  disabled={isOrgOwner || editOrgLoading}
                                  onChange={() => toggleOrgAccess(u.id, oa.orgId, isInOrg)}
                                  style={{ width: 16, height: 16, cursor: isOrgOwner ? 'default' : 'pointer' }}
                                />
                                <div>
                                  <span style={{ fontSize: 13, fontWeight: isCurrent ? 700 : 400, color: t.text }}>{oa.name || oa.orgId.slice(0, 8)}</span>
                                  {isCurrent && <span style={{ fontSize: 10, marginLeft: 6, color: t.accent, fontWeight: 700 }}>(текущая)</span>}
                                  {orgAccess && <span style={{ fontSize: 10, marginLeft: 6, color: t.textMuted }}>{S().admin.orgRoleIn(ROLE_LABELS[orgAccess.role] || orgAccess.role)}</span>}
                                </div>
                              </label>
                            );
                          })}
                          <div style={{ fontSize: 10, color: t.textMuted, marginTop: 4 }}>{S().admin.orgAssignmentHint}</div>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
                    <Btn variant="ghost" onClick={() => setEid(null)} style={{ padding: '10px 18px' }}><X size={16} /> {S().common.cancel}</Btn>
                    <Btn variant="success" onClick={saveE} style={{ padding: '10px 22px' }}><Check size={16} /> {S().common.save}</Btn>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: `${badgeColor}20`, color: badgeColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800 }}>
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: t.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {u.name}
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 800, background: `${badgeColor}15`, color: badgeColor }}>{label}</span>
                      </div>
                      <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <span>{S().admin.loginDisplayLabel} <strong style={{ color: t.textSec }}>{u.login}</strong></span>
                        {u.phone && <>
                          <span style={{color:t.border}}>|</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Phone size={12} />
                            <strong style={{ color: t.textSec }}>{formatPhone(u.phone)}</strong>
                          </span>
                        </>}
                        {/* Этап 2 v2: показываем email сотрудника владельцу. Если у sub-user-а email
                            есть — он может self-recovery через "Забыли пароль". При увольнении
                            владелец должен сменить/удалить email (см. ChangeEmailModal). */}
                        {u.email && <>
                          <span style={{color:t.border}}>|</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Mail size={12} />
                            <strong style={{ color: t.textSec }}>{u.email}</strong>
                          </span>
                        </>}
                        <span style={{color:t.border}}>|</span>
                        <span>
                          {S().admin.passwordDisplayLabel}
                          <strong style={{ color: t.textSec, marginLeft: 6 }}>••••••••</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {isOwner && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button onClick={() => startEdit(u)} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, color: t.textSec, padding: 8 }}><Edit3 size={16} /></button>
                      <button onClick={() => del(u.id)} style={{ background: `${t.accentRed}15`, border: 'none', borderRadius: 8, color: t.accentRed, padding: 8 }}><Trash2 size={16} /></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
    {/* Stage 11: модалка смены логина sub-user-а (только owner). При успехе обновляем
        список юзеров И inline-state редактирования (eu.login), чтобы новый логин был
        виден сразу без перезагрузки страницы. */}
    {loginModalUser && (
      <ChangeLoginModal
        user={loginModalUser}
        orgId={orgId}
        onClose={(refreshList, newLogin) => {
          // Если меняли именно того юзера, который сейчас в edit-mode — обновим eu.login
          // (input в карточке редактирования отображает eu.login).
          if (refreshList && newLogin && eid === loginModalUser.id) {
            setEu(prev => ({ ...prev, login: newLogin }));
          }
          setLoginModalUser(null);
          if (refreshList) refreshUsers();
        }}
      />
    )}
    {/* Этап 2 v2: модалка смены email sub-user-а (только owner). Критично при увольнении —
        очистить email чтобы старый сотрудник не сделал forgot-password. */}
    {emailModalUser && (
      <ChangeEmailModal
        user={emailModalUser}
        orgId={orgId}
        onClose={(refreshList) => {
          setEmailModalUser(null);
          if (refreshList) refreshUsers();
        }}
      />
    )}
    </>
  );
}

// ChangeLoginModal — смена логина sub-user-а (Stage 11, только owner).
// Не трогает пароль/2FA/сессии — это для UX "опечатка" / "переименование".
// Бэк проверяет уникальность; при коллизии возвращает 409 с code='LOGIN_TAKEN'.
function ChangeLoginModal({ user, orgId, onClose }) {
  const t = useTheme();
  const T = S().admin;
  const [login, setLogin] = useState(user.login || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Проверка формата на клиенте — зеркало бэкенда (server/routes/users.js: LOGIN_REGEX + isValidLogin).
  // Спецсимволы [._-] только между alpha-num блоками: `admin.` ❌, `a__b` ❌, `admin_anna` ✓.
  const LOGIN_REGEX = /^[a-zA-Z][a-zA-Z0-9]*(?:[._-][a-zA-Z0-9]+)*$/;
  const isValidFormat = login.length >= 3 && login.length <= 32 && LOGIN_REGEX.test(login);
  const isUnchanged = login === user.login;
  const canSubmit = isValidFormat && !isUnchanged && !busy && !success;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      await api.changeUserLogin(orgId, user.id, login);
      setSuccess(true);
      // Лёгкая пауза — юзер видит "успех", потом закрытие + refresh.
      // Передаём новый login наружу: parent обновит eu.login в edit-mode карточки,
      // чтобы юзер увидел изменение сразу без F5.
      setTimeout(() => onClose(true, login), 900);
    } catch (e) {
      // Локализованные сообщения для известных кодов от бэка
      if (e && e.code === 'LOGIN_TAKEN') setError(T.loginTaken);
      else if (e && e.code === 'LOGIN_INVALID_FORMAT') setError(T.loginInvalidFormat);
      else setError(e?.message || T.errorPrefix || 'Ошибка');
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={busy ? undefined : () => onClose(false)}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, padding: 24, width: 460, maxWidth: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: t.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Edit3 size={18} color={t.accent} />
            {T.changeLoginTitle}
          </div>
          {!busy && <button onClick={() => onClose(false)} style={{ background: 'transparent', color: t.textMuted, padding: 4, border: 'none', cursor: 'pointer' }}><X size={20} /></button>}
        </div>

        <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
          {T.changeLoginDesc}
        </div>

        <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 4 }}>{user.name} — {T.loginDisplayLabel} <strong style={{ color: t.textSec }}>{user.login}</strong></div>

        {success ? (
          <div style={{ background: `${t.accentGreen}15`, color: t.accentGreen, padding: 14, borderRadius: 10, fontSize: 14, fontWeight: 700, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <CheckCircle size={18} /> {T.changeLoginSuccess}
          </div>
        ) : (
          <>
            <input
              type="text"
              autoFocus
              autoComplete="off"
              value={login}
              onChange={e => setLogin(e.target.value.replace(/\s/g, ''))}
              onKeyDown={e => { if (e.key === 'Enter' && canSubmit) submit(); }}
              placeholder={T.changeLoginPlaceholder}
              maxLength={32}
              style={{ width: '100%', padding: '12px 14px', fontSize: 14, fontFamily: 'monospace', marginTop: 6, marginBottom: 8 }}
              disabled={busy}
            />

            {/* Inline-валидация формата — показываем только если что-то введено и формат не подходит */}
            {login.length > 0 && !isValidFormat && (
              <div style={{ fontSize: 12, color: t.accentRed, marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 2 }} /> {T.loginInvalidFormat}
              </div>
            )}
            {isUnchanged && login.length > 0 && (
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 8 }}>{T.loginUnchanged}</div>
            )}

            {error && (
              <div style={{ background: `${t.accentRed}15`, color: t.accentRed, padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} /> {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <Btn onClick={() => onClose(false)} disabled={busy}>{T.cancel || S().common.cancel}</Btn>
              <Btn variant="success" onClick={submit} disabled={!canSubmit}>
                {busy ? '...' : T.changeLoginSubmit}
              </Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ChangeEmailModal — установка/удаление email sub-user'а владельцем (Stage 11/Этап 2 v2).
// Email sub-user-а нужен ему для self-recovery через "Забыли пароль". При увольнении
// владелец должен очистить или заменить email — иначе уволенный сотрудник может
// получить новый reset-link на свой старый email и снова войти в аккаунт.
//
// Без OTP — это admin-action. Owner авторизован, бэк проверяет роль.
function ChangeEmailModal({ user, orgId, onClose }) {
  const t = useTheme();
  const T = S().admin;
  const [email, setEmail] = useState(user.email || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Простая клиентская валидация email (зеркало бэка: содержит @ и точку, длина > 5).
  // Пустая строка = удаление email — это валидно.
  const trimmed = email.trim().toLowerCase();
  const isEmpty = trimmed === '';
  const isValidEmail = isEmpty || (trimmed.includes('@') && trimmed.includes('.') && trimmed.length >= 5);
  const isUnchanged = trimmed === (user.email || '').toLowerCase();
  const canSubmit = isValidEmail && !isUnchanged && !busy && !success;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      // Передаём null для удаления, иначе — нормализованный email
      await api.changeUserEmail(orgId, user.id, isEmpty ? null : trimmed);
      setSuccess(isEmpty ? (T.changeEmailRemoveSuccess || 'Email удалён') : (T.changeEmailSuccess || 'Email обновлён'));
      // Лёгкая пауза — юзер видит "успех", потом закрытие + refresh
      setTimeout(() => onClose(true), 900);
    } catch (e) {
      if (e && e.code === 'EMAIL_TAKEN') setError(T.emailTaken || 'Этот email уже используется другим пользователем');
      else if (e && e.code === 'EMAIL_INVALID') setError(T.emailInvalid || 'Некорректный формат email');
      else setError(e?.message || 'Ошибка');
      setBusy(false);
    }
  };

  // Quick-action: удалить email (то же что submit с пустой строкой)
  const removeEmail = async () => {
    if (busy) return;
    if (!user.email) { onClose(false); return; } // и так пусто
    if (!window.confirm((T.changeEmailRemoveBtn || 'Удалить email') + '?')) return;
    setBusy(true);
    setError('');
    try {
      await api.changeUserEmail(orgId, user.id, null);
      setSuccess(T.changeEmailRemoveSuccess || 'Email удалён');
      setTimeout(() => onClose(true), 900);
    } catch (e) {
      setError(e?.message || 'Ошибка');
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={busy ? undefined : () => onClose(false)}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, padding: 24, width: 480, maxWidth: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: t.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Mail size={18} color={t.accent} />
            {T.changeEmailTitle || 'Email сотрудника'}
          </div>
          {!busy && <button onClick={() => onClose(false)} style={{ background: 'transparent', color: t.textMuted, padding: 4, border: 'none', cursor: 'pointer' }}><X size={20} /></button>}
        </div>

        {/* Описание-предупреждение про increasing security */}
        <div style={{ fontSize: 12, color: t.accentOrange || t.accent, marginBottom: 14, padding: '10px 12px', background: `${t.accentOrange || t.accent}10`, borderRadius: 8, border: `1px solid ${t.accentOrange || t.accent}30`, lineHeight: 1.45, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{T.changeEmailDesc || 'Email сотрудника используется для самостоятельного восстановления пароля. При увольнении обязательно очистите или замените email.'}</span>
        </div>

        <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 4 }}>
          {user.name} — {user.email ? <strong style={{ color: t.textSec }}>{user.email}</strong> : <em>{T.emailNotSet || 'не указан'}</em>}
        </div>

        {success ? (
          <div style={{ background: `${t.accentGreen}15`, color: t.accentGreen, padding: 14, borderRadius: 10, fontSize: 14, fontWeight: 700, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 10 }}>
            <CheckCircle size={18} /> {success}
          </div>
        ) : (
          <>
            <input
              type="email"
              autoFocus
              autoComplete="off"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && canSubmit) submit(); }}
              placeholder={T.changeEmailPlaceholder || 'employee@example.com'}
              maxLength={120}
              style={{ width: '100%', padding: '12px 14px', fontSize: 14, marginTop: 6, marginBottom: 8 }}
              disabled={busy}
            />

            {/* Inline-валидация: показываем ошибку формата если введено что-то невалидное */}
            {!isEmpty && !isValidEmail && (
              <div style={{ fontSize: 12, color: t.accentRed, marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 2 }} /> {T.emailInvalid || 'Некорректный формат email'}
              </div>
            )}

            {error && (
              <div style={{ background: `${t.accentRed}15`, color: t.accentRed, padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} /> {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 8, flexWrap: 'wrap' }}>
              {/* Кнопка "Удалить email" — quick-action, видна только если email сейчас задан */}
              {user.email && (
                <Btn variant="danger" onClick={removeEmail} disabled={busy}>
                  <Trash2 size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                  {T.changeEmailRemoveBtn || 'Удалить email'}
                </Btn>
              )}
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                <Btn onClick={() => onClose(false)} disabled={busy}>{S().common.cancel || 'Отмена'}</Btn>
                <Btn variant="success" onClick={submit} disabled={!canSubmit}>
                  {busy ? '...' : (T.changeEmailSubmit || 'Сохранить')}
                </Btn>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════ Вкладка: СИСТЕМА И БЕКАПЫ ══════════════════════
function ASys({ data, uOrg, user, isOwner, isCompact }) {
  const t = useTheme();
  
  const exp = () => {
    const b = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const u = URL.createObjectURL(b);
    const a = document.createElement('a');
    a.href = u;
    a.download = `inmonex-backup-${data.name}-${fmtDay(new Date())}.json`;
    a.click();
    URL.revokeObjectURL(u);
  };
  
  const imp = e => {
    if (!isOwner) { alert(S().admin.restoreOwnerOnly); return; }
    const f = e.target.files[0];
    if (!f) return;
    if (!window.confirm(S().admin.restoreConfirm)) return;
    
    const r = new FileReader();
    r.onload = ev => {
      try {
        const d = JSON.parse(ev.target.result);
        if (d.accounts && d.transactions) { 
          uOrg(d); 
          addAuditLog(uOrg, user, { 
            type: 'system_import', 
            desc: `База данных восстановлена из файла`,
            details: `Компания в бэкапе: ${d.name || 'неизвестно'}`
          });
          alert(S().admin.restoreSuccess)
        }
        else alert(S().admin.restoreFormatError)
      } catch { alert(S().admin.restoreReadError) }
    };
    r.readAsText(f);
  };

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <Card>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Save size={18} color={t.accent} /> {S().admin.backupTitle}
        </div>
        <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>
          {S().admin.backupHint}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Btn onClick={exp} style={{ padding: '10px 16px' }}><Download size={16} /> {S().admin.saveBackup}</Btn>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 14, cursor: isOwner ? 'pointer' : 'not-allowed', fontWeight: 600, opacity: isOwner ? 1 : 0.6 }}>
            <Save size={16} color={t.accentPurple} /> {S().admin.restoreBackup}
            <input type="file" accept=".json" onChange={imp} style={{ display: 'none' }} disabled={!isOwner} />
          </label>
        </div>
      </Card>

      <Card>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <History size={18} color={t.accentGreen} /> {S().admin.auditTitle}
        </div>
        <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>
          {S().admin.auditHint}
        </div>
        <Btn variant="ghost" onClick={() => downloadAuditLog(data)} style={{ padding: '10px 16px', background: `${t.accentGreen}15`, color: t.accentGreen }}>
          <Download size={16} /> {S().admin.downloadAudit}
        </Btn>
      </Card>

      <Card>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{S().admin.statsTitle}</div>
        <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr 1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <div style={{ padding: 14, background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}` }}><div style={{ fontSize: 12, color: t.textMuted }}>{S().admin.statsUsers}</div><div style={{ fontSize: 20, fontWeight: 800 }}>{(data.users || []).length}</div></div>
          <div style={{ padding: 14, background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}` }}><div style={{ fontSize: 12, color: t.textMuted }}>{S().admin.statsAccounts}</div><div style={{ fontSize: 20, fontWeight: 800 }}>{data.accounts.filter(a => !a.archived).length}</div></div>
          <div style={{ padding: 14, background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}` }}><div style={{ fontSize: 12, color: t.textMuted }}>{S().admin.statsFunds}</div><div style={{ fontSize: 20, fontWeight: 800 }}>{data.funds.filter(f => !f.archived).length}</div></div>
          <div style={{ padding: 14, background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}` }}><div style={{ fontSize: 12, color: t.textMuted }}>{S().admin.statsTx}</div><div style={{ fontSize: 20, fontWeight: 800 }}>{data.transactions.length}</div></div>
        </div>
      </Card>
    </div>
  );
}
